import React, { useEffect, useRef, useState } from "react";
import {
  useStripe,
  useElements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from "@stripe/react-stripe-js";
import classes from "./PurchaseOptions.module.css";
import CommonButton from "../../ui/CommonButton";
import CustomDropdown from "../CustomDropdown/CustomDropdown";
import { IoChevronDownOutline } from "react-icons/io5";
import { env } from "../../constants/env";
import { toast } from "react-toastify";

const token = localStorage.getItem("userToken");

const PurchaseOptions = ({
  setShowPurchase,
  challengeTitle = "$50K",
  challengePrice = 350,
}) => {
  const challengeOptions = [
    { label: "$10K", price: 100 },
    { label: "$25K", price: 200 },
    { label: "$50K", price: 350 },
    { label: "$100K", price: 500 },
  ];

  const [selectedChallenge, setSelectedChallenge] = useState({
    label: challengeTitle,
    price: challengePrice,
  });
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [propcover, setPropcover] = useState(false);
  const [martingale, setMartingale] = useState(false);
  const [showPaymentOptions, setShowPaymentOptions] = useState(false);
  const [showComplete, setShowComplete] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [isLoading, setIsLoading] = useState(false)

  const stripe = useStripe();
  const elements = useElements();
  const dropdownRef = useRef();

  const basePrice = selectedChallenge.price;
  const propcoverPrice = propcover ? basePrice * 0.2 : 0;
  const martingalePrice = martingale ? basePrice * 0.15 : 0;
  const total = basePrice + propcoverPrice + martingalePrice;

  const stripeInputOptions = {
    style: {
      base: {
        color: "#ffffff",
        fontSize: "14px",
        "::placeholder": {
          color: "#AFAFAF",
        },
      },
      invalid: {
        color: "#ff4d4f",
      },
    },
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    }

    if (dropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownOpen]);

  useEffect(() => {
    if (selectedPaymentMethod?.value === "paypal" && !window.paypal) {
      const script = document.createElement("script");
      script.src = `https://www.paypal.com/sdk/js?client-id=${env.paypalClientId}&currency=USD&intent=authorize`;
      script.async = true;
      script.onload = () => console.log("PayPal SDK loaded");
      document.body.appendChild(script);
    }
  }, [selectedPaymentMethod]);

  const checkAvailability = async () => {
    const res = await fetch(`${env.apiUrl}/api/purchase/check-account-availability`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ accountSize: selectedChallenge.label.replace("$", "") }),
    });

    const data = await res.json();

    if (!data.available) {
      toast.error("No available trading accounts for the selected challenge at the moment.");
      return false;
    }

    return true;
  };

  const handleStripePayment = async () => {
    try {
      const res = await fetch(`${env.apiUrl}/api/purchase/create-payment-intent`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          amount: total * 100,
          metadata: {
            challenge: selectedChallenge.label.replace("$", ""),
            propcover: propcoverPrice,
            martingale: martingalePrice,
          },
        }),
      });

      const { clientSecret } = await res.json();

      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement(CardNumberElement),
        },
      });

      if (result.error) {
        toast.error(result.error.message);
      } else if (result.paymentIntent.status === "succeeded") {
        setShowComplete(true);
      }
    } catch (err) {
      console.error(err);
      alert("Payment failed");
    }
  };

  const handlePaypalPayment = async () => {
    try {
      // 1. Create order on backend, get orderID and approvalUrl
      const createRes = await fetch(`${env.apiUrl}/api/purchase/paypal/create-order`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          amount: total,
          metadata: {
            challenge: selectedChallenge.label.replace("$", ""),
            propcover: propcoverPrice,
            martingale: martingalePrice,
          },
        }),
      });

      const createData = await createRes.json();

      if (!createData.id || !createData.approvalUrl) {
        toast.error("Failed to create PayPal order.");
        return;
      }

      // 2. Redirect user to approval URL to approve payment
      window.location.href = createData.approvalUrl;

      // After approval, PayPal redirects user back to your app, where you capture the payment.

    } catch (err) {
      console.error(err);
      toast.error("PayPal payment creation failed");
    }
  };

  const handleCompletePayment = async () => {
    setIsLoading(true)
    const isAvailable = await checkAvailability();
    if (!isAvailable) return;

    if (selectedPaymentMethod?.value === "card") {
      await handleStripePayment();
    } else if (selectedPaymentMethod?.value === "paypal") {
      await handlePaypalPayment();
    }
    setIsLoading(false)
  };

  return showComplete ? (
    <div className="d-flex justify-content-center align-items-center mt-5">
      <div className={`d-flex flex-column align-items-center ${classes.boxedContainer}`}>
        <img src="/icons/check-icon.svg" alt="Success" />
        <p>
          You successfully purchased a {selectedChallenge.label} Challenge account
        </p>
        <span onClick={() => setShowPurchase(false)} role="button" tabIndex={0}>
          Back to home
        </span>
      </div>
    </div>
  ) : (
    <div className={classes.container}>
      <h2 className={`mb-3 ${classes.heading}`}>Purchase</h2>
      <div className={classes.box}>
        <div className={classes.left}>
          {showPaymentOptions ? (
            <div className="d-flex align-items-start">
              <div className={classes.inputsWrapper}>
                <CustomDropdown
                  label="Select payment method"
                  value={selectedPaymentMethod}
                  onChange={setSelectedPaymentMethod}
                  options={[
                    { value: "card", label: "Credit/Debit card" },
                    { value: "paypal", label: "PayPal" },
                  ]}
                  placeholder="Select Payment"
                  inputClass={classes.inputContainer}
                />

                {selectedPaymentMethod?.value === "card" && (
                  <>
                    <div className="mt-2">
                      <label className={classes.label}>Card number</label>
                      <CardNumberElement
                        className={`text-white ${classes.stripeInputContainer}`}
                        options={stripeInputOptions}
                      />
                    </div>
                    <div className="row mt-2">
                      <div className="col-6">
                        <label className={classes.label}>Expiry</label>
                        <CardExpiryElement
                          className={classes.stripeInputContainer}
                          options={stripeInputOptions}
                        />
                      </div>
                      <div className="col-6">
                        <label className={classes.label}>CVV</label>
                        <CardCvcElement
                          className={classes.stripeInputContainer}
                          options={stripeInputOptions}
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
              <div className={classes.verticleDivider} />
            </div>
          ) : (
            <>
              <h4 className={classes.sectionTitle}>Optional</h4>
              <div className={classes.optionWrapper}>
                <div className={classes.option}>
                  <label className={classes.optionLabel}>
                    <input
                      type="checkbox"
                      checked={propcover}
                      onChange={() => setPropcover(!propcover)}
                    />
                    <div className={classes.optionContent}>
                      <div className={`${classes.optionHeader} d-flex justify-content-between align-items-center mb-1`}>
                        <span className={classes.optionTitle}>Propcover</span>
                        <span className={classes.recommended}>Recommended</span>
                      </div>
                      <div className="d-flex justify-content-between align-items-center">
                        <p className={classes.optionDescription}>
                          Trade risk free: get reimbursed when your funded
                          account hits drawdown. Terms Apply*
                        </p>
                        <span className={classes.percent}>+20%</span>
                      </div>
                    </div>
                  </label>
                </div>

                <div className={classes.option}>
                  <label className={classes.optionLabel}>
                    <input
                      type="checkbox"
                      checked={martingale}
                      onChange={() => setMartingale(!martingale)}
                    />
                    <div className={classes.optionContent}>
                      <div className={`${classes.optionHeader} d-flex justify-content-between align-items-center mb-1`}>
                        <span className={classes.optionTitle}>Martingale</span>
                      </div>
                      <div className="d-flex justify-content-between align-items-center">
                        <p className={classes.optionDescription}>
                          Unlock martingale trading at a 1.5x multiplier cap;
                          for more aggressive strategies.
                        </p>
                        <span className={classes.percent}>+15%</span>
                      </div>
                    </div>
                  </label>
                </div>
              </div>
            </>
          )}
        </div>

        <div className={classes.right}>
          <h4 className={classes.sectionTitle}>Summary</h4>
          <div className={classes.summaryRow} style={{ position: "relative" }} ref={dropdownRef}>
            <span
              className={`d-flex align-items-center gap-1 ${classes.challengeDropdownToggle}`}
              onClick={() => !showPaymentOptions && setDropdownOpen(!dropdownOpen)}
            >
              {selectedChallenge.label} Challenge Account
              {!showPaymentOptions && <IoChevronDownOutline color="#CECECE" />}
            </span>
            <span>${basePrice}</span>

            {dropdownOpen && !showPaymentOptions && (
              <div className={classes.challengeDropdownMenu}>
                {challengeOptions
                  .filter((option) => option.label !== selectedChallenge.label)
                  .map((option) => (
                    <div
                      key={option.label}
                      onClick={() => {
                        setSelectedChallenge(option);
                        setDropdownOpen(false);
                      }}
                      className={classes.challengeDropdownItem}
                    >
                      {option.label} Challenge Account
                    </div>
                  ))}
              </div>
            )}
          </div>

          {propcover && (
            <div className={classes.summaryRow}>
              <span>Propcover</span>
              <span>${propcoverPrice}</span>
            </div>
          )}
          {martingale && (
            <div className={classes.summaryRow}>
              <span>Martingale</span>
              <span>${martingalePrice}</span>
            </div>
          )}

          <div className={classes.divider} />
          <div className={`${classes.summaryRow} ${classes.total}`}>
            <span>Total</span>
            <span>${total}</span>
          </div>

          {!showPaymentOptions ? (
            <CommonButton
              type="button"
              text="Continue to payment"
              className={classes.continueBtn}
              onClick={() => setShowPaymentOptions(true)}
              disabled={false}
            />
          ) : (
            <CommonButton
              type="button"
              text={isLoading ? "Loading..." : "Complete"}
              className={classes.continueBtn}
              onClick={handleCompletePayment}
              disabled={!selectedPaymentMethod || isLoading}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default PurchaseOptions;